import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../../theme/webLayoutWidths';

import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  DeviceEventEmitter,
} from 'react-native';
import { Button, HelperText, TextInput, IconButton } from 'react-native-paper';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useDispatch } from 'react-redux';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import useAuth from '../../../hooks/useAuth';
import { useAppSelector } from '../../../redux/helper';
import { RootState } from '../../../redux/types';
import { useTheme, GTFonts } from '../../../theme';
import {
  s,
  fs,
  iconSize,
  v,
  formatNumericStringWithCommas,
  sanitizeMoneyInput,
  sanitizeNumericInput,
  roundToNumericPrecision,
} from '../../../utils/libs';
import ProductImagePicker from './components/ProductImagePicker';
import ProductUnitAffix from './components/ProductUnitAffix';
import { PriceTypeTiles } from './components/FullProductFields';
import { ServiceChargeTiles } from './components/ServiceProductFields';
import {
  getServiceChargeType,
  getServicePriceType,
  getServiceUnit,
  isServiceProduct,
} from '../../../utils/productKind';
import { updateEmployeeProductDetails } from '../../../api/products';
import { mergeProductFields, type ProductVariant } from '../../../redux/productSlice';
import ErrorContext from '../../../Context/ErrorContext';
import { KeyboardStickyView } from 'react-native-keyboard-controller';
import { Picker } from '@react-native-picker/picker';
import useCameraPermissionOnFocus from '../../../hooks/useCameraPermissionOnFocus';
import { usePickerItemColor } from '../../../hooks/usePickerItemColor';
import KeyboardAwareFormScroll from '../../../components/Reusables/KeyboardAwareFormScroll';
import TestLocationBanner from '../../../components/Reusables/TestLocationBanner';
import { hasLegacyItemMassFields } from '../../../utils/hasLegacyItemMassFields';
import { isEmployeeAdminRole } from '../../../utils/roles';
import appAnalytics from '../../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../../services/appAnalyticsContext';
import ReviewService from '../../../services/ReviewService';

type RouteParams = { productId?: string };

const getProductVariants = (product: any): any[] =>
  Array.isArray(product?.bp_product_variants) ? product.bp_product_variants : [];

const getVariantDisplayName = (variant: any): string => {
  if (!variant) return 'Variant';
  const explicitName = String(variant?.bp_variant_display_name || '').trim();
  if (explicitName) return explicitName;
  const values = Object.values(variant?.bp_variant_attributes || {}).filter(
    value => String(value || '').trim().length > 0,
  );
  if (values.length) return values.join(' / ');
  const sku = String(variant?.bp_variant_sku || '').trim();
  if (sku) return sku;
  return 'Variant';
};

type EditableVariant = Omit<
  ProductVariant,
  | 'bp_variant_buying_price'
  | 'bp_variant_selling_price'
  | 'bp_variant_quantity'
  | 'bp_variant_minimum_stock_level'
> & {
  bp_variant_buying_price: number | string;
  bp_variant_selling_price: number | string;
  bp_variant_quantity: number | string;
  bp_variant_minimum_stock_level: number | string;
};

const parseVariantAttributeNames = (value: string): string[] =>
  Array.from(
    new Set(
      String(value || '')
        .split(',')
        .map(name => name.trim())
        .filter(Boolean),
    ),
  );

const syncVariantAttributes = (
  variants: EditableVariant[],
  attributeNames: string[],
): EditableVariant[] =>
  variants.map(variant => {
    const currentAttributes = variant.bp_variant_attributes || {};
    const nextAttributes: Record<string, string> = {};
    attributeNames.forEach(attributeName => {
      nextAttributes[attributeName] = String(
        currentAttributes[attributeName] || '',
      );
    });
    return { ...variant, bp_variant_attributes: nextAttributes };
  });

const EmployeeAddProductImage: React.FC = () => {
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  const { theme } = useTheme();
  const { pickerItemColor, pickerItemColorSecondary } = usePickerItemColor();
  const styles = useMemo(() => createStyles(theme), [theme]);
  useCameraPermissionOnFocus();
  const inputOutlineProps = useMemo(
    () => ({
      outlineColor: theme.colors.borderGray,
      activeOutlineColor: theme.colors.primary,
    }),
    [theme],
  );

  // Web-only: subtle placeholder/label styling via theme
  const webPlaceholderTheme = useMemo(() => {
    if (Platform.OS === 'web') {
      const hexToRgba = (hex: string, alpha: number) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };
      const placeholderColor = theme.colors.textSecondary
        ? hexToRgba(theme.colors.textSecondary, 0.55)
        : 'rgba(128, 128, 128, 0.55)';
      return {
        theme: {
          fonts: {
            regular: { fontFamily: GTFonts.regular, fontWeight: '400' as any },
          },
          colors: {
            placeholder: placeholderColor,
            onSurfaceVariant: placeholderColor,
          },
        },
      };
    }
    return {};
  }, [theme.colors.textSecondary]);
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const dispatch = useDispatch();
  const { setSnackMessage } = useContext(ErrorContext);
  const productId = (route?.params as RouteParams)?.productId || '';
  const employee = useAppSelector(
    (state: RootState) => state.auth.inApp.account.employeeInfo,
  );

  const isAdmin = isEmployeeAdminRole(employee?.bp_employee_role);
  const products = useAppSelector(
    (s: RootState) => s.products.products.docs,
  ) as any[];
  const product = products?.find(p => p?._id === productId);

  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [priceType, setPriceType] = useState<'item' | 'unit'>(
    (product?.bp_product_price_type as 'item' | 'unit') || 'item',
  );
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [showProductImage, setShowProductImage] = useState(
    Platform.OS !== 'web',
  );
  const [showAdditional, setShowAdditional] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [variantRows, setVariantRows] = useState<EditableVariant[]>([]);
  const [variantAttributeNames, setVariantAttributeNames] = useState<string[]>(
    [],
  );
  const scanTargetVariantId = useRef<string | null>(null);

  const [form, setForm] = useState({
    productname: String(product?.bp_product_name || ''),
    category: String(product?.bp_product_category || ''),
    unitofmass: String(product?.bp_product_unit_of_mass || ''),
    buyingprice: String(product?.bp_product_buying_price ?? ''),
    sellingprice: String(product?.bp_product_selling_price ?? ''),
    quantity:
      product?.bp_product_quantity == null
        ? ''
        : String(roundToNumericPrecision(Number(product.bp_product_quantity))),
    mass: String(product?.bp_product_mass ?? ''),
    size: String(product?.bp_product_size || ''),
    model: String(product?.bp_product_model || ''),
    msl: String(product?.bp_minimum_stock_level ?? ''),
    barcode: String(product?.bp_product_barcode || ''),
    bp_product_is_favourite: product?.bp_product_is_favourite || false,
    bp_product_image: String(product?.bp_product_image || ''),
  });
  const hasVariants = useMemo(
    () => Boolean(product?.bp_product_has_variants && variantRows.length > 0),
    [product?.bp_product_has_variants, variantRows.length],
  );
  // A service keeps no stock: no Inventory, barcode, size or model; its
  // charge type takes the sell type's place and its cost is optional
  const isService = isServiceProduct(product);
  const servicePerHour = isService && priceType === 'unit';
  // Edit-form legacy support: show mass/unit only when product already has them.
  const showLegacyItemMassFields = useMemo(
    () => hasLegacyItemMassFields(product),
    // Freeze per product identity so clearing fields mid-edit does not hide them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [product?._id],
  );
  const selectedVariant = useMemo(
    () => variantRows.find(variant => variant?._id === selectedVariantId) || null,
    [selectedVariantId, variantRows],
  );

  const itemMassRequiresUnit = useMemo(() => {
    if (!isAdmin) return false;
    if (priceType !== 'item') return false;
    // Only enforce when legacy mass fields are visible on this edit form.
    if (!showLegacyItemMassFields) return false;
    const massStr = String(form.mass ?? '').trim();
    if (!massStr) return false;
    const massNum = Number(massStr);
    if (!Number.isFinite(massNum) || massNum <= 0) return false;
    return !String(form.unitofmass ?? '').trim();
  }, [isAdmin, priceType, form.mass, form.unitofmass, showLegacyItemMassFields]);

  useEffect(() => {
    if (product) {
      console.log('product details', product);
      const incomingVariants = getProductVariants(product).map(variant => ({
        ...variant,
        bp_variant_buying_price: String(variant?.bp_variant_buying_price ?? ''),
        bp_variant_selling_price: String(variant?.bp_variant_selling_price ?? ''),
        bp_variant_quantity: String(variant?.bp_variant_quantity ?? ''),
        bp_variant_minimum_stock_level: String(
          variant?.bp_variant_minimum_stock_level ?? '',
        ),
      })) as EditableVariant[];
      const baseAttributes = Array.isArray(product?.bp_product_variant_attributes)
        ? product.bp_product_variant_attributes
        : [];
      const derivedAttributeNames =
        baseAttributes.length > 0
          ? baseAttributes
          : Array.from(
              new Set(
                incomingVariants.flatMap(variant =>
                  Object.keys(variant.bp_variant_attributes || {}),
                ),
              ),
            );
      setForm({
        productname: String(product?.bp_product_name || ''),
        category: String(product?.bp_product_category || ''),
        unitofmass: String(product?.bp_product_unit_of_mass || ''),
        buyingprice: String(product?.bp_product_buying_price ?? ''),
        sellingprice: String(product?.bp_product_selling_price ?? ''),
        quantity:
          product?.bp_product_quantity == null
            ? ''
            : String(
                roundToNumericPrecision(Number(product.bp_product_quantity)),
              ),
        mass: String(product?.bp_product_mass ?? ''),
        size: String(product?.bp_product_size || ''),
        model: String(product?.bp_product_model || ''),
        msl: String(product?.bp_minimum_stock_level ?? ''),
        barcode: String(product?.bp_product_barcode || ''),
        bp_product_is_favourite: product?.bp_product_is_favourite || false,
        bp_product_image: String(product?.bp_product_image || ''),
      });
      setPriceType(
        (product?.bp_product_price_type as 'item' | 'unit') || 'item',
      );
      setVariantRows(incomingVariants);
      setVariantAttributeNames(derivedAttributeNames);
      const defaultVariantId = String(incomingVariants[0]?._id || '');
      setSelectedVariantId(defaultVariantId);
    }
  }, [productId, product]);

  useEffect(() => {
    if (!hasVariants) return;
    const hasSelected = variantRows.some(
      variant => variant?._id === selectedVariantId,
    );
    if (!hasSelected) {
      setSelectedVariantId(String(variantRows[0]?._id || ''));
    }
  }, [hasVariants, selectedVariantId, variantRows]);

  const handleInputChange = useCallback(
    (key: keyof typeof form, value: string) => {
      setForm(prev => ({ ...prev, [key]: value }));
    },
    [],
  );

  const setVariantField = useCallback(
    (
      targetVariantId: string,
      field: keyof ProductVariant,
      value: string | number | Record<string, string>,
    ) => {
      setVariantRows(prev =>
        prev.map(variant =>
          String(variant?._id || '') === targetVariantId
            ? ({ ...variant, [field]: value } as EditableVariant)
            : variant,
        ),
      );
    },
    [],
  );

  const setVariantAttributeValue = useCallback(
    (targetVariantId: string, attributeName: string, value: string) => {
      setVariantRows(prev =>
        prev.map(variant => {
          if (String(variant?._id || '') !== targetVariantId) return variant;
          return {
            ...variant,
            bp_variant_attributes: {
              ...(variant.bp_variant_attributes || {}),
              [attributeName]: value,
            },
          };
        }),
      );
    },
    [],
  );

  const updateVariantAttributeNamesFromInput = useCallback((value: string) => {
    const parsed = parseVariantAttributeNames(value);
    setVariantAttributeNames(parsed);
    setVariantRows(prev => syncVariantAttributes(prev, parsed));
  }, []);

  useEffect(() => {
    const sub = DeviceEventEmitter.addListener(
      'BARCODE_SCANNED',
      (code: string) => {
        const targetVariantId = scanTargetVariantId.current;
        if (targetVariantId) {
          scanTargetVariantId.current = null;
          setVariantRows(prev =>
            prev.map(variant =>
              String(variant?._id || '') === targetVariantId
                ? { ...variant, bp_variant_barcode: code }
                : variant,
            ),
          );
        } else {
          setForm(prev => ({ ...prev, barcode: code }));
        }
      },
    );
    return () => sub.remove();
  }, []);

  const handleProductImageUploaded = useCallback((b64: string) => {
    setForm(prev => ({ ...prev, bp_product_image: b64 }));
  }, []);

  const handleDiscardProductImage = useCallback(() => {
    setForm(prev => ({ ...prev, bp_product_image: '' }));
  }, []);

  const onSave = useCallback(async () => {
    if (!productId) {
      setSnackMessage({
        message: 'Missing product id',
        type: 'error',
        visible: true,
      });
      return;
    }
    if (!isAdmin && !form.bp_product_image) {
      setSnackMessage({
        message: 'Please select an image first',
        type: 'error',
        visible: true,
      });
      return;
    }
    if (hasVariants && !isAdmin && !selectedVariantId) {
      setSnackMessage({
        message: 'Select a variant before saving image',
        type: 'error',
        visible: true,
      });
      return;
    }
    setClicked(true);
    try {
      // Sub-category is no longer editable in the form.
      // On update, always pass the existing stored value (or empty string).
      const existingSubCategory = String(
        product?.bp_product_sub_category || '',
      );
      const variantsPayload = hasVariants
        ? variantRows
            .filter(v => !(v as any)._softDeleted)
            .map(variant => {
            const attributes: Record<string, string> = {};
            variantAttributeNames.forEach(attributeName => {
              attributes[attributeName] = String(
                variant.bp_variant_attributes?.[attributeName] || '',
              );
            });
            return {
              ...(variant?._id ? { _id: variant._id } : {}),
              bp_variant_sku: String(variant.bp_variant_sku || ''),
              bp_variant_attributes: attributes,
              bp_variant_barcode: String((variant.bp_variant_barcode || '').trim()),
              bp_variant_buying_price: Number(variant.bp_variant_buying_price || 0),
              bp_variant_selling_price: Number(variant.bp_variant_selling_price || 0),
              bp_variant_quantity: Number(variant.bp_variant_quantity || 0),
              bp_variant_image: String(variant.bp_variant_image || ''),
              bp_variant_minimum_stock_level: Number(
                variant.bp_variant_minimum_stock_level || 0,
              ),
              bp_variant_display_name: String(variant.bp_variant_display_name || ''),
            };
          })
        : [];
      const payload = isAdmin
        ? {
            bp_product_id: productId,
            bp_product_name: form.productname,
            bp_product_category: form.category,
            bp_product_sub_category: existingSubCategory,
            bp_product_unit_of_mass: form.unitofmass,
            bp_product_buying_price: hasVariants ? '' : form.buyingprice,
            bp_product_selling_price: hasVariants ? '' : form.sellingprice,
            // A service keeps no stock: the server ignores these, sent blank
            bp_product_quantity: hasVariants || isService ? '' : form.quantity,
            bp_product_mass: form.mass,
            bp_product_size: form.size,
            bp_product_model: form.model,
            bp_minimum_stock_level: hasVariants || isService ? '' : form.msl,
            bp_product_price_type: priceType,
            bp_product_barcode:
              hasVariants || isService ? '' : (form.barcode || '').trim(),
            bp_product_is_favourite: form.bp_product_is_favourite,
            bp_product_image: String(
              form.bp_product_image ||
                (hasVariants
                  ? String(
                      variantRows.find(
                        v => !(v as any)._softDeleted && v.bp_variant_image,
                      )?.bp_variant_image || '',
                    )
                  : ''),
            ),
            bp_product_has_variants: hasVariants,
            bp_product_variant_attributes: hasVariants ? variantAttributeNames : [],
            bp_product_variants: hasVariants ? variantsPayload : [],
            ...(hasVariants && selectedVariantId
              ? { bp_variant_id: selectedVariantId }
              : {}),
          }
        : {
            bp_product_id: productId,
            bp_product_name: '',
            bp_product_category: '',
            bp_product_sub_category: '',
            bp_product_unit_of_mass: '',
            bp_product_buying_price: '',
            bp_product_selling_price: '',
            bp_product_quantity: '',
            bp_product_mass: '',
            bp_product_size: '',
            bp_product_model: '',
            bp_minimum_stock_level: '',
            bp_product_price_type: '',
            bp_product_barcode: '',
            bp_product_is_favourite: false,
            bp_product_image: String(
              form.bp_product_image ||
                (hasVariants
                  ? String(
                      variantRows.find(
                        v => !(v as any)._softDeleted && v.bp_variant_image,
                      )?.bp_variant_image || '',
                    )
                  : ''),
            ),
            ...(hasVariants && selectedVariantId
              ? { bp_variant_id: selectedVariantId }
              : {}),
          };

      const response = await updateEmployeeProductDetails(
        axiosPrivate,
        String(employee?._id || ''),
        String(productId),
        payload,
        auth,
      );
      console.log('updateEmployeeProductDetailsResponse', response);
      if (response?.status === 200) {
        appAnalytics.track('catalog_product_updated', {
          ...buildAppAnalyticsContext({
            role: employee?.bp_employee_role,
          }),
          source: 'pos',
        });
        ReviewService.onEligibleAction('PRODUCT_UPDATED');
        const remoteImage =
          response?.data?.data?.bp_product_image ||
          response?.data?.bp_product_image ||
          response?.data?.url ||
          response?.data?.secure_url ||
          '';
        const updatedVariants = hasVariants
          ? isAdmin
            ? variantRows.filter(v => !(v as any)._softDeleted)
            : variantRows.map(variant =>
                variant?._id === selectedVariantId
                  ? {
                      ...variant,
                      bp_variant_image: remoteImage || form.bp_product_image,
                    }
                  : variant,
              )
          : [];
        const updatedVariantsForStore = updatedVariants.map(variant => ({
          ...(variant?._id ? { _id: variant._id } : {}),
          bp_variant_sku: String(variant.bp_variant_sku || ''),
          bp_variant_attributes: variant.bp_variant_attributes || {},
          bp_variant_barcode: String((variant.bp_variant_barcode || '').trim()),
          bp_variant_buying_price: Number(variant.bp_variant_buying_price || 0),
          bp_variant_selling_price: Number(variant.bp_variant_selling_price || 0),
          bp_variant_quantity: Number(variant.bp_variant_quantity || 0),
          bp_variant_image: String(variant.bp_variant_image || ''),
          bp_variant_minimum_stock_level: Number(
            variant.bp_variant_minimum_stock_level || 0,
          ),
          bp_variant_display_name: String(variant.bp_variant_display_name || ''),
        }));
        if (isAdmin) {
          dispatch(
            mergeProductFields({
              productId: String(productId),
              updates: {
                bp_product_name: form.productname,
                bp_product_category: form.category,
                bp_product_sub_category: existingSubCategory,
                bp_product_unit_of_mass: form.unitofmass,
                // A service's cost left blank is unset, not 0
                bp_product_buying_price: hasVariants
                  ? null
                  : isService && !String(form.buyingprice).trim()
                    ? null
                    : Number(form.buyingprice) || 0,
                bp_product_selling_price: hasVariants
                  ? null
                  : Number(form.sellingprice) || 0,
                bp_product_quantity:
                  hasVariants || isService ? null : Number(form.quantity) || 0,
                bp_product_mass: form.mass,
                bp_product_size: form.size,
                bp_product_model: form.model,
                bp_minimum_stock_level: isService ? 0 : Number(form.msl) || 0,
                bp_product_price_type: priceType,
                bp_product_barcode: hasVariants ? '' : (form.barcode || '').trim(),
                bp_product_is_favourite: form.bp_product_is_favourite,
                bp_product_image: remoteImage || form.bp_product_image,
                ...(hasVariants
                  ? {
                      bp_product_has_variants: true,
                      bp_product_variant_attributes: variantAttributeNames,
                      bp_product_variants: updatedVariantsForStore,
                    }
                  : {}),
              },
            }),
          );
          setSnackMessage({
            message: 'Product updated',
            type: 'success',
            visible: true,
          });
        } else {
          dispatch(
            mergeProductFields({
              productId: String(productId),
              updates: {
                bp_product_image: remoteImage || form.bp_product_image,
                ...(hasVariants
                  ? { bp_product_variants: updatedVariantsForStore }
                  : {}),
              },
            }),
          );
          setSnackMessage({
            message: 'Image updated',
            type: 'success',
            visible: true,
          });
        }
        navigation.goBack();
      } else {
        setSnackMessage({
          message: response?.data?.message || 'Failed to save changes',
          type: 'error',
          visible: true,
        });
      }
    } catch (e: any) {
      console.log('updateEmployeeProductDetailsError', e);
      setSnackMessage({
        message: e?.message || 'Failed to save changes',
        type: 'error',
        visible: true,
      });
    } finally {
      setClicked(false);
    }
  }, [
    axiosPrivate,
    auth,
    employee?._id,
    form,
    hasVariants,
    isAdmin,
    priceType,
    product,
    productId,
    selectedVariantId,
    setSnackMessage,
    variantRows,
    variantAttributeNames,
    navigation,
    dispatch,
  ]);

  if (!productId || !product) {
    return (
      <View
        style={[
          styles.safeArea,
          { alignItems: 'center', justifyContent: 'center' },
        ]}>
        <Text style={styles.sectionTitle}>Product not found</Text>
      </View>
    );
  }

  const inputsDisabled = !isAdmin;
  const disabledStyle = inputsDisabled ? { opacity: 0.5 } : null;

  return (
    <View style={styles.safeArea}>
      <KeyboardAwareFormScroll
        style={styles.sheetContent}
        contentContainerStyle={styles.scrollContainer}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        bottomOffset={v(240)}>
        <TestLocationBanner />
        <View style={styles.formCard}>
            <Text style={styles.subtitleText}>
              {isService
                ? 'You are editing a service.'
                : `You are editing a ${hasVariants ? 'Variant' : 'Simple'} product.`}
            </Text>
            {!hasVariants && Platform.OS === 'web' ? (
              <View>
                <TouchableOpacity
                  onPress={() => setShowProductImage(prev => !prev)}
                  style={styles.accordionHeader}>
                  <Text style={[styles.accordionTitle, { flex: 1 }]}>
                    Product Image
                  </Text>
                  <IconButton
                    icon="chevron-down"
                    size={iconSize(20)}
                    iconColor={theme.colors.text}
                    style={[
                      styles.accordionChevron,
                      {
                        transform: [
                          { rotate: showProductImage ? '180deg' : '0deg' },
                        ],
                      },
                    ]}
                  />
                </TouchableOpacity>
                {showProductImage ? (
                  <ProductImagePicker
                    previewUri={form.bp_product_image}
                    onUploaded={handleProductImageUploaded}
                    onDiscard={handleDiscardProductImage}
                    onProcessingChange={setIsProcessingImage}
                  />
                ) : null}
              </View>
            ) : !hasVariants ? (
              <View>
                <Text style={styles.sectionTitle}>Product Image</Text>
                <ProductImagePicker
                  previewUri={form.bp_product_image}
                  onUploaded={handleProductImageUploaded}
                  onDiscard={handleDiscardProductImage}
                  onProcessingChange={setIsProcessingImage}
                />
              </View>
            ) : null}

            {hasVariants && !isAdmin ? (
              <View style={{ gap: v(6) }}>
                <Text style={styles.sectionTitle}>Variant</Text>
                <View style={styles.pickerContainer}>
                  <Picker
                    selectedValue={selectedVariantId}
                    onValueChange={value =>
                      setSelectedVariantId(String(value || ''))
                    }
                    dropdownIconColor={theme.colors.primary}
                    mode="dropdown"
                    style={styles.picker}>
                    <Picker.Item
                      label="Select variant"
                      value=""
                      color={pickerItemColorSecondary}
                    />
                    {variantRows.map(variant => (
                      <Picker.Item
                        key={String(variant?._id)}
                        label={getVariantDisplayName(variant)}
                        value={String(variant?._id || '')}
                        color={pickerItemColor}
                      />
                    ))}
                  </Picker>
                </View>
                <Text style={styles.variantHintText}>
                  {`Updating image for: ${getVariantDisplayName(selectedVariant)}`}
                </Text>
              </View>
            ) : null}

            <View>
            <Text style={styles.sectionTitle}>Product Details</Text>
            <View style={{ gap: v(12) }}>
            <TextInput
              mode="outlined"
              label="Product Name *"
              value={form.productname}
              placeholderTextColor={theme.colors.textSecondary}
              {...inputOutlineProps}
              {...webPlaceholderTheme}
              style={[styles.input, disabledStyle]}
              editable={!inputsDisabled}
              onChangeText={t => handleInputChange('productname', t)}
            />
            <TextInput
              mode="outlined"
              label="Category *"
              value={form.category}
              onChangeText={t => handleInputChange('category', t)}
              placeholderTextColor={theme.colors.textSecondary}
              {...inputOutlineProps}
              {...webPlaceholderTheme}
              style={[styles.input, disabledStyle]}
              editable={!inputsDisabled}
              right={
                isAdmin ? (
                  <TextInput.Icon
                    icon="chevron-down"
                    color={theme.colors.primary}
                    onPress={() => setCategoryModalVisible(true)}
                  />
                ) : undefined
              }
            />
            </View>
            </View>

            <View style={{ gap: v(14) }}>
              {isService ? (
                <ServiceChargeTiles
                  chargeType={getServiceChargeType(priceType)}
                  onChange={charge => {
                    // Per hour is a per-unit item whose unit is the hour
                    setPriceType(getServicePriceType(charge));
                    setForm(prev => ({
                      ...prev,
                      mass: '',
                      unitofmass: getServiceUnit(charge),
                    }));
                  }}
                  disabled={!isAdmin}
                />
              ) : (
                <PriceTypeTiles
                  priceType={priceType}
                  onChangePriceType={value => {
                    const next = value === 'unit' ? 'unit' : 'item';
                    setPriceType(next);
                    // Avoid stale unit/mass when fields are hidden for non-legacy item products.
                    if (next === 'unit') {
                      setForm(prev => ({ ...prev, mass: '' }));
                    } else if (!showLegacyItemMassFields) {
                      setForm(prev => ({ ...prev, mass: '', unitofmass: '' }));
                    }
                  }}
                  disabled={!isAdmin}
                />
              )}
            </View>

            {/* Edit-form: sell-per-unit always shows unit picker. Sell-per-item
                mass/unit inputs only when this product already has legacy values. */}
            {isService ? null : priceType === 'unit' ? (
              <View style={{ marginBottom: v(12) }}>
                <View style={[styles.pickerContainer, disabledStyle]}>
                  <Picker
                    selectedValue={form.unitofmass}
                    onValueChange={value =>
                      handleInputChange('unitofmass', String(value))
                    }
                    dropdownIconColor={theme.colors.primary}
                    mode="dropdown"
                    enabled={isAdmin}
                    style={styles.picker}>
                    <Picker.Item
                      label="Select Unit of Mass"
                      value=""
                      color={pickerItemColorSecondary}
                    />
                    {[
                      'Mg',
                      'g',
                      'Kg',
                      'Ml',
                      'Lt',
                      'inch',
                      'Ft',
                      'Mm',
                      'Cm',
                      'M',
                    ].map(u => (
                      <Picker.Item
                        key={u}
                        label={u}
                        value={u}
                        color={pickerItemColor}
                      />
                    ))}
                  </Picker>
                </View>
              </View>
            ) : showLegacyItemMassFields ? (
              <>
                <TextInput
                  mode="outlined"
                  label="Mass/Volume"
                  placeholder="Mass/Volume i.e 250"
                  value={form.mass}
                  onChangeText={t =>
                    handleInputChange('mass', t.replace(/[^0-9.]/g, ''))
                  }
                  keyboardType="numeric"
                  placeholderTextColor={theme.colors.textSecondary}
                  {...inputOutlineProps}
                  {...webPlaceholderTheme}
                  style={[styles.input, disabledStyle]}
                  editable={!inputsDisabled}
                />
                <View style={[styles.pickerContainer, disabledStyle]}>
                  <Picker
                    selectedValue={form.unitofmass}
                    onValueChange={value =>
                      handleInputChange('unitofmass', String(value))
                    }
                    dropdownIconColor={theme.colors.primary}
                    mode="dropdown"
                    enabled={isAdmin}
                    style={styles.picker}>
                    <Picker.Item
                      label="Select Unit of Mass"
                      value=""
                      color={pickerItemColorSecondary}
                    />
                    {[
                      'Mg',
                      'g',
                      'Kg',
                      'Ml',
                      'Lt',
                      'inch',
                      'Ft',
                      'Mm',
                      'Cm',
                      'M',
                    ].map(u => (
                      <Picker.Item
                        key={u}
                        label={u}
                        value={u}
                        color={pickerItemColor}
                      />
                    ))}
                  </Picker>
                </View>
              </>
            ) : null}

            {hasVariants && isAdmin ? (
              <View style={styles.variantSection}>
                <View>
                <Text style={styles.sectionTitle}>Variant Attributes</Text>
                <View style={{ gap: v(10) }}>
                <TextInput
                  mode="outlined"
                  label="Variant Attributes (comma separated)"
                  value={variantAttributeNames.join(', ')}
                  onChangeText={updateVariantAttributeNamesFromInput}
                  placeholder="Color, Size"
                  placeholderTextColor={theme.colors.textSecondary}
                  {...inputOutlineProps}
                  {...webPlaceholderTheme}
                  style={styles.input}
                />
                </View>
                </View>
                <View>
                <Text style={styles.sectionTitle}>Variants</Text>
                {variantRows.map((variant, index) => {
                  const variantId = String(variant?._id || `variant-${index}`);
                  return (
                    <View
                      key={variantId}
                      style={[
                        styles.variantCard,
                        (variant as any)._softDeleted
                          ? styles.variantCardDeleted
                          : null,
                      ]}>
                      <View style={styles.variantCardHeader}>
                        <Text style={styles.variantCardTitle}>
                          Variant {index + 1}
                        </Text>
                        <TouchableOpacity
                          style={styles.variantDeleteTouchable}
                          onPress={() =>
                            setVariantRows(prev =>
                              prev.map(v =>
                                String(v?._id || '') === variantId
                                  ? { ...v, _softDeleted: !(v as any)._softDeleted } as any
                                  : v,
                              ),
                            )
                          }
                          accessibilityLabel={
                            (variant as any)._softDeleted
                              ? 'Restore variant'
                              : 'Remove variant'
                          }>
                          <Text
                            style={[
                              styles.variantDeleteLabel,
                              {
                                color: (variant as any)._softDeleted
                                  ? theme.colors.successDark
                                  : theme.colors.errorDark,
                              },
                            ]}>
                            {(variant as any)._softDeleted ? 'Restore' : 'Delete'}
                          </Text>
                          <MaterialCommunityIcons
                            name={
                              (variant as any)._softDeleted
                                ? 'backup-restore'
                                : 'trash-can-outline'
                            }
                            size={iconSize(18)}
                            color={
                              (variant as any)._softDeleted
                                ? theme.colors.successDark
                                : theme.colors.errorDark
                            }
                          />
                        </TouchableOpacity>
                      </View>
                      <ProductImagePicker
                        previewUri={String(variant.bp_variant_image || '')}
                        onUploaded={(b64: string) =>
                          setVariantField(variantId, 'bp_variant_image', b64)
                        }
                        onDiscard={() =>
                          setVariantField(variantId, 'bp_variant_image', '')
                        }
                      />
                      {variantAttributeNames.map(attributeName => (
                        <TextInput
                          key={`${variantId}-${attributeName}`}
                          mode="outlined"
                          label={`${attributeName} *`}
                          value={String(
                            variant.bp_variant_attributes?.[attributeName] || '',
                          )}
                          onChangeText={text =>
                            setVariantAttributeValue(variantId, attributeName, text)
                          }
                          placeholderTextColor={theme.colors.textSecondary}
                          {...inputOutlineProps}
                          {...webPlaceholderTheme}
                          style={styles.input}
                        />
                      ))}
                      <View style={styles.variantRow}>
                        <TextInput
                          mode="outlined"
                          label="Buying Price *"
                          value={formatNumericStringWithCommas(String(variant.bp_variant_buying_price ?? ''))}
                          onChangeText={text =>
                            setVariantField(
                              variantId,
                              'bp_variant_buying_price',
                              sanitizeMoneyInput(text),
                            )
                          }
                          keyboardType="numeric"
                          placeholderTextColor={theme.colors.textSecondary}
                          {...inputOutlineProps}
                          {...webPlaceholderTheme}
                          style={[styles.input, styles.variantInputHalf]}
                          right={ProductUnitAffix({
                            priceType,
                            unitOfMass: form.unitofmass,
                            kind: 'price',
                          })}
                        />
                        <TextInput
                          mode="outlined"
                          label="Selling Price *"
                          value={formatNumericStringWithCommas(String(variant.bp_variant_selling_price ?? ''))}
                          onChangeText={text =>
                            setVariantField(
                              variantId,
                              'bp_variant_selling_price',
                              sanitizeMoneyInput(text),
                            )
                          }
                          keyboardType="numeric"
                          placeholderTextColor={theme.colors.textSecondary}
                          {...inputOutlineProps}
                          {...webPlaceholderTheme}
                          style={[styles.input, styles.variantInputHalf]}
                          right={ProductUnitAffix({
                            priceType,
                            unitOfMass: form.unitofmass,
                            kind: 'price',
                          })}
                        />
                      </View>
                      <View style={styles.variantRow}>
                        <TextInput
                          mode="outlined"
                          label="Quantity *"
                          value={formatNumericStringWithCommas(String(variant.bp_variant_quantity ?? ''))}
                          onChangeText={text =>
                            setVariantField(
                              variantId,
                              'bp_variant_quantity',
                              sanitizeNumericInput(text, {
                                isUnit: priceType === 'unit',
                              }),
                            )
                          }
                          keyboardType="numeric"
                          placeholderTextColor={theme.colors.textSecondary}
                          {...inputOutlineProps}
                          {...webPlaceholderTheme}
                          style={[styles.input, styles.variantInputHalf]}
                          right={ProductUnitAffix({
                            priceType,
                            unitOfMass: form.unitofmass,
                            kind: 'stock',
                          })}
                        />
                        <TextInput
                          mode="outlined"
                          label="Minimum Stock"
                          value={formatNumericStringWithCommas(String(variant.bp_variant_minimum_stock_level ?? ''))}
                          onChangeText={text =>
                            setVariantField(
                              variantId,
                              'bp_variant_minimum_stock_level',
                              sanitizeNumericInput(text, {
                                isUnit: priceType === 'unit',
                              }),
                            )
                          }
                          keyboardType="numeric"
                          placeholderTextColor={theme.colors.textSecondary}
                          {...inputOutlineProps}
                          {...webPlaceholderTheme}
                          style={[styles.input, styles.variantInputHalf]}
                          right={ProductUnitAffix({
                            priceType,
                            unitOfMass: form.unitofmass,
                            kind: 'stock',
                          })}
                        />
                      </View>
                      <TextInput
                        mode="outlined"
                        label="SKU"
                        value={String(variant.bp_variant_sku || '')}
                        onChangeText={text =>
                          setVariantField(variantId, 'bp_variant_sku', text)
                        }
                        placeholderTextColor={theme.colors.textSecondary}
                        {...inputOutlineProps}
                        {...webPlaceholderTheme}
                        style={styles.input}
                      />
                      <TextInput
                        mode="outlined"
                        label="Barcode"
                        value={String(variant.bp_variant_barcode || '')}
                        onChangeText={text =>
                          setVariantField(variantId, 'bp_variant_barcode', text.trim())
                        }
                        placeholderTextColor={theme.colors.textSecondary}
                        {...inputOutlineProps}
                        {...webPlaceholderTheme}
                        style={styles.input}
                        right={
                          <TextInput.Icon
                            icon="barcode-scan"
                            color={theme.colors.primary}
                            onPress={() => {
                              scanTargetVariantId.current = variantId;
                              navigation.navigate('BarcodeCapture' as never);
                            }}
                          />
                        }
                      />
                    </View>
                  );
                })}
                </View>
              </View>
            ) : hasVariants ? (
              <>
                {variantAttributeNames.length ? (
                  <View style={styles.variantSection}>
                    <View>
                    <Text style={styles.sectionTitle}>Variant Attributes</Text>
                    <View style={{ gap: v(10) }}>
                    {variantAttributeNames.map(attributeName => (
                      <TextInput
                        key={attributeName}
                        mode="outlined"
                        label={`${attributeName} *`}
                        value={String(
                          selectedVariant?.bp_variant_attributes?.[attributeName] || '',
                        )}
                        onChangeText={text => {
                          if (!selectedVariantId) return;
                          setVariantAttributeValue(
                            String(selectedVariantId),
                            attributeName,
                            text,
                          );
                        }}
                        placeholderTextColor={theme.colors.textSecondary}
                        {...inputOutlineProps}
                        {...webPlaceholderTheme}
                        style={[styles.input, disabledStyle]}
                        editable={!inputsDisabled}
                      />
                    ))}
                    </View>
                    </View>
                  </View>
                ) : null}

                <View>
                <Text style={styles.sectionTitle}>Pricing</Text>
                <View style={{ flexDirection: 'row', gap: s(8) }}>
                  <TextInput
                    mode="outlined"
                    label="Buying Price *"
                    value={inputsDisabled ? '' : formatNumericStringWithCommas(String(selectedVariant?.bp_variant_buying_price ?? ''))}
                    onChangeText={t => {
                      if (!selectedVariantId) return;
                      setVariantField(
                        String(selectedVariantId),
                        'bp_variant_buying_price',
                        sanitizeMoneyInput(t),
                      );
                    }}
                    keyboardType="numeric"
                    placeholderTextColor={theme.colors.textSecondary}
                    {...inputOutlineProps}
                    {...webPlaceholderTheme}
                    style={[styles.input, { flex: 1 }, disabledStyle]}
                    editable={!inputsDisabled}
                    right={ProductUnitAffix({
                      priceType,
                      unitOfMass: form.unitofmass,
                      kind: 'price',
                    })}
                  />
                  <TextInput
                    mode="outlined"
                    label="Selling Price *"
                    value={inputsDisabled ? '' : formatNumericStringWithCommas(String(selectedVariant?.bp_variant_selling_price ?? ''))}
                    onChangeText={t => {
                      if (!selectedVariantId) return;
                      setVariantField(
                        String(selectedVariantId),
                        'bp_variant_selling_price',
                        sanitizeMoneyInput(t),
                      );
                    }}
                    keyboardType="numeric"
                    placeholderTextColor={theme.colors.textSecondary}
                    {...inputOutlineProps}
                    {...webPlaceholderTheme}
                    style={[styles.input, { flex: 1 }, disabledStyle]}
                    editable={!inputsDisabled}
                    right={ProductUnitAffix({
                      priceType,
                      unitOfMass: form.unitofmass,
                      kind: 'price',
                    })}
                  />
                </View>
                </View>

                <View>
                <Text style={styles.sectionTitle}>Inventory</Text>
                <View style={{ flexDirection: 'row', gap: s(8) }}>
                  <TextInput
                    mode="outlined"
                    label="Quantity *"
                    value={inputsDisabled ? '' : formatNumericStringWithCommas(String(selectedVariant?.bp_variant_quantity ?? ''))}
                    onChangeText={t => {
                      if (!selectedVariantId) return;
                      setVariantField(
                        String(selectedVariantId),
                        'bp_variant_quantity',
                        sanitizeNumericInput(t, {
                          isUnit: priceType === 'unit',
                        }),
                      );
                    }}
                    keyboardType="numeric"
                    placeholderTextColor={theme.colors.textSecondary}
                    {...inputOutlineProps}
                    {...webPlaceholderTheme}
                    style={[styles.input, { flex: 1 }, disabledStyle]}
                    editable={!inputsDisabled}
                    right={ProductUnitAffix({
                      priceType,
                      unitOfMass: form.unitofmass,
                      kind: 'stock',
                    })}
                  />
                  <TextInput
                    mode="outlined"
                    label="Minimum Stock Level"
                    value={inputsDisabled ? '' : formatNumericStringWithCommas(String(selectedVariant?.bp_variant_minimum_stock_level ?? ''))}
                    onChangeText={t => {
                      if (!selectedVariantId) return;
                      setVariantField(
                        String(selectedVariantId),
                        'bp_variant_minimum_stock_level',
                        sanitizeNumericInput(t, {
                          isUnit: priceType === 'unit',
                        }),
                      );
                    }}
                    keyboardType="numeric"
                    placeholderTextColor={theme.colors.textSecondary}
                    {...inputOutlineProps}
                    {...webPlaceholderTheme}
                    style={[styles.input, { flex: 1 }, disabledStyle]}
                    editable={!inputsDisabled}
                    right={ProductUnitAffix({
                      priceType,
                      unitOfMass: form.unitofmass,
                      kind: 'stock',
                    })}
                  />
                </View>
                </View>
              </>
            ) : (
              <>
                <View>
                <Text style={styles.sectionTitle}>Pricing</Text>
                <View style={{ flexDirection: 'row', gap: s(8) }}>
                  <TextInput
                    mode="outlined"
                    label={
                      isService
                        ? servicePerHour
                          ? 'Cost per hour (optional)'
                          : 'Cost (optional)'
                        : priceType === 'unit'
                          ? 'Buying Price per Unit*'
                          : 'Buying Price *'
                    }
                    value={inputsDisabled ? '' : formatNumericStringWithCommas(form.buyingprice)}
                    onChangeText={t =>
                      handleInputChange('buyingprice', sanitizeMoneyInput(t))
                    }
                    keyboardType="numeric"
                    placeholderTextColor={theme.colors.textSecondary}
                    {...inputOutlineProps}
                    {...webPlaceholderTheme}
                    style={[styles.input, { flex: 1 }, disabledStyle]}
                    editable={!inputsDisabled}
                    right={ProductUnitAffix({
                      priceType,
                      unitOfMass: form.unitofmass,
                      kind: 'price',
                    })}
                  />
                  <TextInput
                    mode="outlined"
                    label={
                      isService
                        ? servicePerHour
                          ? 'Rate per hour *'
                          : 'Selling Price *'
                        : priceType === 'unit'
                          ? 'Selling Price per Unit*'
                          : 'Selling Price *'
                    }
                    value={inputsDisabled ? '' : formatNumericStringWithCommas(form.sellingprice)}
                    onChangeText={t =>
                      handleInputChange('sellingprice', sanitizeMoneyInput(t))
                    }
                    keyboardType="numeric"
                    placeholderTextColor={theme.colors.textSecondary}
                    {...inputOutlineProps}
                    {...webPlaceholderTheme}
                    style={[styles.input, { flex: 1 }, disabledStyle]}
                    editable={!inputsDisabled}
                    right={ProductUnitAffix({
                      priceType,
                      unitOfMass: form.unitofmass,
                      kind: 'price',
                    })}
                  />
                </View>
                </View>

                {!isService ? (
                <View>
                <Text style={styles.sectionTitle}>Inventory</Text>
                <View style={{ flexDirection: 'row', gap: s(8) }}>
                  <TextInput
                    mode="outlined"
                    label="Quantity *"
                    value={inputsDisabled ? '' : formatNumericStringWithCommas(form.quantity)}
                    onChangeText={t =>
                      handleInputChange(
                        'quantity',
                        sanitizeNumericInput(t, {
                          isUnit: priceType === 'unit',
                        }),
                      )
                    }
                    keyboardType="numeric"
                    placeholderTextColor={theme.colors.textSecondary}
                    {...inputOutlineProps}
                    {...webPlaceholderTheme}
                    style={[styles.input, { flex: 1 }, disabledStyle]}
                    editable={!inputsDisabled}
                    right={ProductUnitAffix({
                      priceType,
                      unitOfMass: form.unitofmass,
                      kind: 'stock',
                    })}
                  />
                  <TextInput
                    mode="outlined"
                    label="Minimum Stock Level"
                    value={inputsDisabled ? '' : formatNumericStringWithCommas(form.msl)}
                    onChangeText={t =>
                      handleInputChange(
                        'msl',
                        sanitizeNumericInput(t, {
                          isUnit: priceType === 'unit',
                        }),
                      )
                    }
                    keyboardType="numeric"
                    placeholderTextColor={theme.colors.textSecondary}
                    {...inputOutlineProps}
                    {...webPlaceholderTheme}
                    style={[styles.input, { flex: 1 }, disabledStyle]}
                    editable={!inputsDisabled}
                    right={ProductUnitAffix({
                      priceType,
                      unitOfMass: form.unitofmass,
                      kind: 'stock',
                    })}
                  />
                </View>
                </View>
                ) : null}
              </>
            )}

            {/* A service has no barcode, size or model: nothing to show here */}
            {!isService ? (
            <View>
            <TouchableOpacity
              onPress={() => setShowAdditional(prev => !prev)}
              style={[styles.accordionHeader, hasVariants && { marginTop: v(6) }]}>
              <Text style={[styles.accordionTitle]}>
                Additional Information
              </Text>
              <IconButton
                icon="chevron-down"
                size={iconSize(20)}
                iconColor={theme.colors.text}
                style={[
                  styles.accordionChevron,
                  {
                    transform: [{ rotate: showAdditional ? '180deg' : '0deg' }],
                  },
                ]}
              />
            </TouchableOpacity>

            {showAdditional ? (
              <View style={{ gap: v(10) }}>
                {hasVariants ? (
                  <>
                    <Text style={styles.variantParentImageLabel}>
                      Product Thumbnail (optional)
                    </Text>
                    <Text style={styles.variantParentImageHint}>
                      Falls back to first variant image if not set
                    </Text>
                    <ProductImagePicker
                      previewUri={form.bp_product_image}
                      onUploaded={handleProductImageUploaded}
                      onDiscard={handleDiscardProductImage}
                      onProcessingChange={setIsProcessingImage}
                    />
                    <View style={{ flexDirection: 'row', gap: s(8) }}>
                      <TextInput
                        mode="outlined"
                        label="Barcode (optional)"
                        value={String(selectedVariant?.bp_variant_barcode || '')}
                        onChangeText={t => {
                          if (!selectedVariantId) return;
                          setVariantField(
                            String(selectedVariantId),
                            'bp_variant_barcode',
                            t,
                          );
                        }}
                        placeholderTextColor={theme.colors.textSecondary}
                        {...inputOutlineProps}
                        {...webPlaceholderTheme}
                        style={[styles.input, { flex: 1 }, disabledStyle]}
                        editable={!inputsDisabled}
                        right={
                          isAdmin ? (
                            <TextInput.Icon
                              icon="barcode-scan"
                              color={theme.colors.primary}
                              onPress={() => {
                                scanTargetVariantId.current = String(
                                  selectedVariantId || '',
                                );
                                navigation.navigate('BarcodeCapture' as never);
                              }}
                            />
                          ) : undefined
                        }
                      />
                      <TextInput
                        mode="outlined"
                        label="SKU"
                        value={String(selectedVariant?.bp_variant_sku || '')}
                        onChangeText={t => {
                          if (!selectedVariantId) return;
                          setVariantField(String(selectedVariantId), 'bp_variant_sku', t);
                        }}
                        placeholderTextColor={theme.colors.textSecondary}
                        {...inputOutlineProps}
                        {...webPlaceholderTheme}
                        style={[styles.input, { flex: 1 }, disabledStyle]}
                        editable={!inputsDisabled}
                      />
                    </View>
                  </>
                ) : isService ? null : (
                  <>
                    <TextInput
                      mode="outlined"
                      label="Barcode (optional)"
                      value={form.barcode}
                      onChangeText={t => handleInputChange('barcode', t)}
                      placeholderTextColor={theme.colors.textSecondary}
                      {...inputOutlineProps}
                      {...webPlaceholderTheme}
                      style={[styles.input, disabledStyle]}
                      editable={!inputsDisabled}
                      right={
                        isAdmin ? (
                          <TextInput.Icon
                            icon="barcode-scan"
                            color={theme.colors.primary}
                            onPress={() => navigation.navigate('BarcodeCapture' as never)}
                          />
                        ) : undefined
                      }
                    />
                    {form.barcode ? (
                      <HelperText
                        type={
                          /^[A-Za-z0-9]{4,}$/.test(form.barcode.trim())
                            ? 'info'
                            : 'error'
                        }
                        visible>
                        {/^[A-Za-z0-9]{4,}$/.test(form.barcode.trim())
                          ? null
                          : 'Alphanumeric only, at least 8 characters'}
                      </HelperText>
                    ) : null}
                  </>
                )}

                {!hasVariants && !isService && priceType === 'item' ? (
                  <View style={[styles.pickerContainer, disabledStyle]}>
                    <Picker
                      selectedValue={form.size}
                      onValueChange={value =>
                        handleInputChange('size', String(value))
                      }
                      dropdownIconColor={theme.colors.primary}
                      mode="dropdown"
                      enabled={isAdmin}
                      style={styles.picker}>
                      <Picker.Item
                        label="Size"
                        value=""
                        color={pickerItemColorSecondary}
                      />
                      {[
                        { value: 'xsmall', label: 'Extra Small' },
                        { value: 'small', label: 'Small' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'large', label: 'Large' },
                        { value: 'xlarge', label: 'Extra Large' },
                        { value: '2xlarge', label: 'Extra Extra Large' },
                        {
                          value: '3xlarge',
                          label: 'Three Extra Extra Large',
                        },
                        { value: '4xlarge', label: 'Four Extra Extra Large' },
                      ].map(opt => (
                        <Picker.Item
                          key={opt.value}
                          label={opt.label}
                          value={opt.value}
                          color={pickerItemColor}
                        />
                      ))}
                    </Picker>
                  </View>
                ) : null}

                {!isService ? (
                  <TextInput
                    mode="outlined"
                    label="Model | Type | Make | Other"
                    value={form.model}
                    onChangeText={t => handleInputChange('model', t)}
                    placeholderTextColor={theme.colors.textSecondary}
                    {...inputOutlineProps}
                    {...webPlaceholderTheme}
                    style={[styles.input, disabledStyle]}
                    editable={!inputsDisabled}
                  />
                ) : null}
              </View>
            ) : null}
            </View>
            ) : null}
        </View>
      </KeyboardAwareFormScroll>
      <KeyboardStickyView offset={{ closed: 0, opened: v(30) }}>
        <View style={styles.footer}>
          <Button
            mode="contained"
            icon="content-save"
            onPress={onSave}
            loading={clicked}
            disabled={
              clicked ||
              isProcessingImage ||
              (isAdmin && itemMassRequiresUnit) ||
              (hasVariants && !isAdmin && !selectedVariantId) ||
              (!isAdmin && !form.bp_product_image)
            }
            uppercase={false}
            buttonColor={theme.colors.primary}
            textColor={theme.colors.textInverse}
            contentStyle={{ height: v(52) }}
            labelStyle={{
              fontFamily: GTFonts.semibold,
              fontSize: fs(16),
            }}
            style={styles.submitBtn}>
            {isAdmin ? 'Save Changes' : 'Save Image'}
          </Button>
        </View>
      </KeyboardStickyView>
    </View>
  );
};

export default EmployeeAddProductImage;

const createStyles = (theme: any) =>
  StyleSheet.create({
    safeArea: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    sheetContent: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    scrollContainer: {
      flexGrow: 1,
      // padding: s(8),
      // paddingBottom: v(20),
      // gap: v(12),
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        padding: s(24),
        gap: v(20),
      }),
    },
    formCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.sm,
      padding: s(12),
      gap: v(18),
      ...theme.shadows.sm,
      ...(Platform.OS === 'web' && {
        padding: s(24),
        gap: v(20),
      }),
    },
    content: {
      padding: s(10),
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        padding: s(24),
      }),
    },
    footer: {
      backgroundColor: theme.colors.surface,
      paddingHorizontal: s(8),
      paddingVertical: s(20),
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        paddingHorizontal: s(24),
        borderTopWidth: 0,
      }),
    },
    sectionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(18),
      marginBottom: v(4),
      color: theme.colors.textDark ?? theme.colors.text,
      ...(Platform.OS === 'web' && {
        fontSize: fs(18),
        marginTop: v(24),
        marginBottom: v(12),
        paddingBottom: v(8),
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.borderGray,
      }),
    },
    accordionHeader: {
      width: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 0,
      marginHorizontal: 0,
      ...(Platform.OS === 'web' && {
        marginTop: v(24),
        marginBottom: v(12),
        paddingBottom: v(8),
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.borderGray,
      }),
    },
    accordionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.textDark ?? theme.colors.text,
      ...(Platform.OS === 'web' && {
        fontSize: fs(18),
      }),
    },
    accordionChevron: {
      margin: 0,
      padding: 0,
    },
    input: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      height: v(50),
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.text,
      ...(Platform.OS === 'web' && {
        height: v(56),
        fontSize: fs(14),
      }),
    } as any,
    fieldLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      color: theme.colors.textDark ?? theme.colors.text,
      marginBottom: v(4),
    },
    pickerContainer: {
      borderWidth: 1,
      borderColor: theme.colors.borderGray,
      // height: v(52),
      borderRadius: theme.borderRadius.md,
      marginVertical: v(6),
      justifyContent: 'center',
      overflow: 'hidden',
      backgroundColor: theme.colors.surface,
      ...(Platform.OS === 'web' && {
        height: v(56),
      }),
    },
    picker: {
      fontSize: fs(14),
      // height: v(52),
      color: theme.colors.text,
      ...(Platform.OS === 'web' && {
        height: v(56),
        borderWidth: 0,
        backgroundColor: 'transparent',
        outlineStyle: 'none',
        outlineWidth: 0,
        outlineColor: 'transparent',
        paddingHorizontal: 12,
        fontSize: fs(14),
      }),
    } as any,
    submitBtn: {
      marginTop: v(8),
      borderRadius: theme.borderRadius.md,
      alignSelf: 'stretch',
    },
    variantSection: {
      gap: v(18),
      marginTop: v(4),
    },
    variantHintText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    subtitleText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
      marginBottom: v(4),
    },
    variantCard: {
      padding: s(14),
      borderRadius: s(12),
      backgroundColor: theme.colors.surfaceSecondary,
      gap: v(10),
      marginTop: v(6),
    },
    variantCardDeleted: {
      opacity: 0.5,
    },
    variantCardHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    } as any,
    variantCardTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      color: theme.colors.text,
    },
    variantDeleteTouchable: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(4),
    } as any,
    variantDeleteLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(16),
    },
    variantRow: {
      flexDirection: 'row',
      gap: s(8),
    } as any,
    variantInputHalf: {
      flex: 1,
    } as any,
    variantParentImageLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      color: theme.colors.textDark ?? theme.colors.text,
    },
    variantParentImageHint: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      color: theme.colors.textSecondary,
      marginTop: v(-6),
    },
    keyboardAvoidingView: { flex: 1 },
    popupOverlay: {
      flexGrow: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(0,0,0,0.5)',
      padding: s(10),
    },
    popupContainer: {
      width: '80%',
      backgroundColor: theme.colors.surface,
      borderRadius: s(8),
      padding: s(15),
    },
    modalBtnRow: {
      flexDirection: 'row',
      gap: s(8),
      marginTop: v(10),
    },
    subcategoryItem: {
      paddingVertical: s(10),
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.borderGray200,
    },
    subcategoryItemText: {
      fontSize: fs(16),
      color: theme.colors.text,
    },
    popupTitle: {
      fontSize: fs(18),
      fontWeight: 'bold',
      marginBottom: v(15),
      textAlign: 'center',
      color: theme.colors.text,
    },
  });
